// api/client.js
// Central API client — all requests go through here
// Automatically attaches JWT token to every request

const BASE_URL = process.env.REACT_APP_API_URL;
const getToken = () => localStorage.getItem("sc_token");

// ── Session expiry, without navigating from here ─────────────────────────────
//
// This module used to answer every 401 with `window.location.href = "/login"`:
// a full page reload, fired from whichever request happened to come back first.
// That was wrong in three separate ways.
//
//   1. It fired for public endpoints and for fire-and-forget telemetry. A
//      reader whose token expired lost their page to a section-view POST they
//      never asked for.
//   2. It threw away the URL they were on, so login had nowhere to send them
//      back to.
//   3. It returned undefined instead of throwing, so every `.catch()` in the
//      app was dead code for the one case it most needed to handle, and
//      callers went on to dereference the undefined.
//
// Enough of the app had already worked around it that the workarounds were the
// documentation: api/training.js, EpisodePlayer.refreshVideoSrc and
// CourseEnrollPage.refreshPreviewSrc all hand-roll a raw fetch purely to dodge
// this function, and the backend made /api/courses/{slug}/progress public for
// the same reason.
//
// So: a 401 now always throws, and never navigates. Navigation is opt-in per
// call via `authRequired`, for the actions that genuinely cannot continue
// without a session (creating and verifying a payment, the admin dashboard).
// The redirect itself is performed by SessionGuard, inside the Router, which is
// the only thing that knows the current URL and can push a history entry
// instead of reloading the document.
let unauthorizedHandler = null;

// Registered by SessionGuard on mount, cleared on unmount.
export const setUnauthorizedHandler = (fn) => {
  unauthorizedHandler = fn;
};

// Several protected calls can be in flight together and all come back 401. One
// redirect is the correct answer to all of them.
let lastNotifiedAt = 0;
const NOTIFY_WINDOW_MS = 1000;

const notifyUnauthorized = () => {
  const now = Date.now();
  if (now - lastNotifiedAt < NOTIFY_WINDOW_MS) return;
  lastNotifiedAt = now;
  if (unauthorizedHandler) unauthorizedHandler();
};

// `authRequired` marks a call that cannot proceed without a signed-in user, and
// is the only thing that may send the browser to login. Everything else — every
// public read, every background write — just throws.
const request = async (method, path, body = null, { authRequired = false } = {}) => {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const options = { method, headers };
  if (body) options.body = JSON.stringify(body);

  const res = await fetch(`${BASE_URL}${path}`, options);

  if (res.status === 401) {
    // Only clear the session if we actually presented one. A 401 on a call that
    // carried no token says nothing about the stored token, and signing someone
    // out because an unrelated endpoint was stricter than expected is its own
    // bug.
    if (token) {
      localStorage.removeItem("sc_token");
      localStorage.removeItem("sc_user");
    }
    const err = new Error("Your session has expired. Please sign in again.");
    // Callers branch on this to show "sign in again" in place, the same way
    // they already branch on 409 to mean "your page is stale".
    err.status = 401;
    err.isAuthError = true;
    if (authRequired) notifyUnauthorized();
    throw err;
  }

  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ message: "Something went wrong" }));
    const err = new Error(body.message || `HTTP ${res.status}`);
    // Callers need the status to tell "this is fine, your page is stale"
    // (409 already owned) apart from a real failure worth alarming about.
    err.status = res.status;
    throw err;
  }

  // No content
  if (res.status === 204) return null;

  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

// Multipart upload. Deliberately NOT routed through request(): that function
// sets Content-Type: application/json and JSON.stringifies the body, and a
// FormData sent that way arrives as "[object Object]". The browser must set
// Content-Type itself here, because only it knows the multipart boundary.
//
// Error and 401 handling is kept identical to request() so callers can treat an
// upload like any other call.
const upload = async (path, formData, { authRequired = false } = {}) => {
  const headers = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (res.status === 401) {
    if (token) {
      localStorage.removeItem("sc_token");
      localStorage.removeItem("sc_user");
    }
    const err = new Error("Your session has expired. Please sign in again.");
    err.status = 401;
    err.isAuthError = true;
    if (authRequired) notifyUnauthorized();
    throw err;
  }

  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ message: "Upload failed" }));
    const err = new Error(body.message || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }

  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

export const apiClient = {
  get: (path, opts) => request("GET", path, null, opts),
  post: (path, body, opts) => request("POST", path, body, opts),
  put: (path, body, opts) => request("PUT", path, body, opts),
  patch: (path, body, opts) => request("PATCH", path, body, opts),
  delete: (path, opts) => request("DELETE", path, null, opts),
  upload: (path, formData, opts) => upload(path, formData, opts),
};

export default apiClient;
